import {
  IssueCaseCategory,
  IssueCaseKind,
  IssueCaseRequestCategory,
  IssueCaseRequestSubcategory,
  IssueCaseStatusCode,
  IssueCaseSubcategory,
  IssueTaskCategory,
  IssueTaskStatus,
} from '@gooduncles/gu-app-schema';

/**
 * caseKind='issue' 와 caseKind='request' 양쪽 카테고리를 아우르는 union 타입.
 * 스키마 통합 이후 콘솔에서 카테고리를 다룰 때 기본 타입.
 */
export type IssueCaseAnyCategory = IssueCaseCategory | IssueCaseRequestCategory;

/**
 * 양쪽 서브카테고리를 아우르는 union 타입.
 */
export type IssueCaseAnySubcategory = IssueCaseSubcategory | IssueCaseRequestSubcategory;

/**
 * DELETED를 제외한 활성 상태 목록
 */
export const ISSUE_CASE_ACTIVE_STATUSES = [
  IssueCaseStatusCode.PENDING,
  IssueCaseStatusCode.OPEN,
  IssueCaseStatusCode.IN_PROGRESS,
  IssueCaseStatusCode.CLOSED,
  IssueCaseStatusCode.CANCELED,
];

/**
 * caseKind('issue' / 'request') 한글 레이블
 * legacy 문서의 null 은 'issue' 로 취급한다.
 */
export const issueCaseKindLabel: Record<IssueCaseKind, string> = {
  issue: '이슈',
  request: '요청',
};

/**
 * caseKind 별 액센트 색상 (IssuePanel 카드 헤더 제목)
 */
export const issueCaseKindColor: Record<IssueCaseKind, string> = {
  issue: '#fa8c16', // 주황
  request: '#0f766e', // 청록
};

/**
 * caseKind 별 antd Tag color (카테고리 네비게이션 배지)
 */
export const issueCaseKindTagColor: Record<IssueCaseKind, string> = {
  issue: 'orange',
  request: 'cyan',
};

/**
 * caseKind 정규화 — legacy 문서의 null/undefined 는 'issue' 로 취급
 */
export const normalizeCaseKind = (caseKind: IssueCaseKind | null | undefined): IssueCaseKind => caseKind ?? 'issue';

/**
 * 이슈 케이스 카테고리 한글 매핑 (caseKind='issue' / 'request' 양쪽 포함)
 */
export const issueCaseCategoryKr: Record<IssueCaseAnyCategory, string> = {
  [IssueCaseCategory.QUALITY]: '품질',
  [IssueCaseCategory.DELIVERY]: '배송',
  [IssueCaseCategory.SUPPLIER]: '거래처',
  [IssueCaseCategory.ORDER]: '주문',
  [IssueCaseRequestCategory.REQUEST_PRODUCT]: '상품 문의',
  [IssueCaseRequestCategory.REQUEST_ACCOUNT]: '계정·서비스',
  [IssueCaseRequestCategory.REQUEST_DELIVERY]: '배송 문의',
  [IssueCaseRequestCategory.REQUEST_PAYMENT]: '정산·결제',
  [IssueCaseRequestCategory.OTHER]: '기타',
};

/**
 * 이슈 태스크 카테고리 한글 매핑
 */
export const issueTaskCategoryKr: Record<IssueTaskCategory, string> = {
  [IssueTaskCategory.DELIVERY]: '배송',
  [IssueTaskCategory.PICK_UP]: '회수',
  [IssueTaskCategory.QUICK]: '퀵',
  [IssueTaskCategory.REFUND]: '환불',
  [IssueTaskCategory.ADDITIONAL_CHARGE]: '추가청구',
  [IssueTaskCategory.OTHER]: '기타',
  [IssueTaskCategory.INTERNAL]: '내부처리',
  [IssueTaskCategory.REQUEST]: '요청 응대',
};

/**
 * 이슈 케이스 서브카테고리 한글 매핑 (카테고리별)
 *
 * caseKind='issue' 와 'request' 양쪽 카테고리를 모두 포함한다.
 * request 카테고리의 OTHER 는 IssueCaseRequestSubcategory.OTHER 만 노출.
 */
export const issueCaseSubcategoryKr: Record<IssueCaseAnyCategory, Record<string, string>> = {
  [IssueCaseCategory.QUALITY]: {
    [IssueCaseSubcategory.DEFECT]: '불량',
    [IssueCaseSubcategory.QUANTITY]: '용량',
    [IssueCaseSubcategory.EXPIRATION_DATE]: '유통기한',
    [IssueCaseSubcategory.OTHER]: '기타',
  },
  [IssueCaseCategory.DELIVERY]: {
    [IssueCaseSubcategory.MISSING]: '누락',
    [IssueCaseSubcategory.WRONG_DELIVERY]: '오배송',
    [IssueCaseSubcategory.DAMAGED]: '파손',
    [IssueCaseSubcategory.COLD_DAMAGE]: '냉해',
    [IssueCaseSubcategory.STORAGE]: '보관',
    [IssueCaseSubcategory.OTHER]: '기타',
  },
  [IssueCaseCategory.SUPPLIER]: {
    [IssueCaseSubcategory.SUPPLIER_MISSING]: '거래처 누락',
    [IssueCaseSubcategory.WRONG_SHIPMENT]: '오출고',
    [IssueCaseSubcategory.OUT_OF_STOCK]: '품절',
    [IssueCaseSubcategory.OTHER]: '기타',
  },
  [IssueCaseCategory.ORDER]: {
    [IssueCaseSubcategory.PRE_ORDER]: '선주문',
    [IssueCaseSubcategory.ORDER_ADJUSTMENT]: '주문 조정',
    [IssueCaseSubcategory.RETURN_OR_EXCHANGE]: '반품/교환',
    [IssueCaseSubcategory.OPERATION_ERROR]: '운영 오류',
    [IssueCaseSubcategory.OTHER]: '기타',
  },
  [IssueCaseRequestCategory.REQUEST_PRODUCT]: {
    [IssueCaseRequestSubcategory.PRODUCT_INFO]: '상품 정보',
    [IssueCaseRequestSubcategory.PRODUCT_STOCK]: '재고/입고',
    [IssueCaseRequestSubcategory.PRODUCT_PRICE]: '가격',
    [IssueCaseRequestSubcategory.NEW_PRODUCT]: '신규 상품',
    [IssueCaseRequestSubcategory.OTHER]: '기타',
  },
  [IssueCaseRequestCategory.REQUEST_ACCOUNT]: {
    [IssueCaseRequestSubcategory.ACCOUNT_CHANGE]: '계정 변경',
    [IssueCaseRequestSubcategory.PASSWORD_RESET]: '비밀번호 초기화',
    [IssueCaseRequestSubcategory.APP_USAGE]: '앱 사용',
    [IssueCaseRequestSubcategory.OTHER]: '기타',
  },
  [IssueCaseRequestCategory.REQUEST_DELIVERY]: {
    [IssueCaseRequestSubcategory.DELIVERY_LOCATION]: '배송지',
    [IssueCaseRequestSubcategory.DELIVERY_TIME]: '배송 시간',
    [IssueCaseRequestSubcategory.OTHER]: '기타',
  },
  [IssueCaseRequestCategory.REQUEST_PAYMENT]: {
    [IssueCaseRequestSubcategory.INVOICE_INQUIRY]: '청구서',
    [IssueCaseRequestSubcategory.PAYMENT_METHOD]: '결제 방법',
    [IssueCaseRequestSubcategory.OTHER]: '기타',
  },
  [IssueCaseRequestCategory.OTHER]: {
    [IssueCaseRequestSubcategory.OTHER]: '기타',
  },
};

/**
 * '기타 인쇄물'에 들어가는 issueTask의 카테고리
 * : 이슈배송은 거래처에서 받아와야 하는 물량이므로 주문 상품과 동일하게 취급한다.
 *   회수/기타는 입고량이 아니라서 제외한다.
 */
export const issueTaskCategoriesForPackingInfoSheet = [IssueTaskCategory.DELIVERY];

/**
 * '패킹표'에 들어가는 issueTask의 카테고리
 */
export const issueTaskCategoriesForPackingSheet = [
  IssueTaskCategory.PICK_UP,
  IssueTaskCategory.DELIVERY,
  IssueTaskCategory.OTHER,
];

/**
 * TaskSpots에 들어가는 issueTask의 카테고리
 */
export const issueTaskCategoriesForTaskSpots = [IssueTaskCategory.DELIVERY, IssueTaskCategory.PICK_UP];

export const issueTaskCategoriesForTargetDate = [IssueTaskCategory.DELIVERY, IssueTaskCategory.PICK_UP];

export const issueTaskStatusForTargetDate = [IssueTaskStatus.IN_PROGRESS, IssueTaskStatus.COMPLETED];

/**
 * 정산(청구서)에 포함되는 issueTask의 카테고리
 */
export const issueTaskCategoriesForSettlement = [IssueTaskCategory.REFUND, IssueTaskCategory.ADDITIONAL_CHARGE];

/**
 * 모든 케이스 분류
 */
export const ISSUE_CASE_ALL_CATEGORIES = Object.values(IssueCaseCategory);

/**
 * DELETED를 제외한 태스크 활성 상태 목록
 */
export const ISSUE_TASK_ACTIVE_STATUSES = [
  IssueTaskStatus.PENDING,
  IssueTaskStatus.OPEN,
  IssueTaskStatus.IN_PROGRESS,
  IssueTaskStatus.COMPLETED,
  IssueTaskStatus.FAILED,
  IssueTaskStatus.CANCELED,
];

/**
 * 청구서에서 관심있는 issueTask의 상태 (수행중이거나 완료된 경우)
 * - 청구서에는 완료된 항목만 포함된다.
 * - 수행중인 항목이 청구서 발행일과 같은 시점이라면 청구서에 포함해야 하므로 담당자에게 완료 변경을 요청한다.
 */
export const ISSUE_TASK_STATUS_FOR_INVOICE = [IssueTaskStatus.IN_PROGRESS, IssueTaskStatus.COMPLETED];

/**
 * 모든 태스크 분류
 */
export const ISSUE_TASK_ALL_CATEGORIES = Object.values(IssueTaskCategory);
